import { deleteJson, fetchJson, patchJson, postJson } from '../client';

export interface DeckListItemDto {
  id: number;
  title: string;
  cardCount: number;
  /** Cards z `state == Review` (FSRS-5 graduated). 0 dla decka bez progresu. */
  masteredCount: number;
  visibility: 'private' | 'unlisted' | 'public';
  language: 'pl' | 'en';
  updatedAt: string;
  tagSlugs: string[];
  /** Owner: own placement (Decks.FolderId). Collaborator: per-user override (UserDeckFolders.FolderId). null = root. */
  folderId: number | null;
  /** Populated tylko dla global search results (`?q=` present); array of folder titles top→leaf. */
  breadcrumbPath: string[] | null;
  /** Owner-vs-collaborator badge support (Sub-A `ownerUserId` na DeckResponse — dla list item potrzebne T22 SharedWithMeSection). */
  ownerUserId: number;
  /** True gdy deck jest subscribed publicznie (read-only, brak edit/delete). Optional dla
   *  backwards-compat z istniejącymi test mocks; backend zawsze zwraca pole. */
  isSubscribed?: boolean;
}

export interface DeckListResponse {
  items: DeckListItemDto[];
}

export interface DeckResponse {
  id: number;
  ownerUserId: number;
  title: string;
  description: string | null;
  visibility: 'private' | 'unlisted' | 'public';
  isPublished: boolean;
  cardCount: number;
  language: 'pl' | 'en';
  coverBlobUrl: string | null;
  dailyNewCardsLimit: number;
  tagSlugs: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateDeckPayload {
  title: string;
  description?: string;
  language?: 'pl' | 'en';
  dailyNewCardsLimit?: number;
  tagSlugs?: string[];
  /** Optional placement — utworzony deck w folderze (musi należeć do caller'a). NULL = root. */
  folderId?: number | null;
}

export type PatchDeckPayload = Partial<CreateDeckPayload>;

export interface AddCollaboratorPayload {
  email: string;
  role: 'Viewer' | 'Editor';
}

export interface DeckCollaboratorResponse {
  deckId: number;
  userId: number;
  role: string;
  invitedAt: string;
  acceptedAt: string | null;
}

// === Public catalog ===

export interface PublicDeckItemDto {
  id: number;
  title: string;
  description: string | null;
  language: 'pl' | 'en';
  cardCount: number;
  subscriberCount: number;
  createdAt: string;
  ownerUserId: number;
  ownerUsername: string;
  ownerDisplayName: string;
  ownerAvatarUrl: string | null;
  tagSlugs: string[];
}

export interface PublicDeckListResponse {
  items: PublicDeckItemDto[];
  /** Opaque cursor — pass back as `?cursor=` na następną stronę. null = no more pages. */
  nextCursor: string | null;
}

export interface PublicDecksQuery {
  q?: string;
  /** Comma-separated tag slugs (OR semantics). */
  tags?: string[];
  /** Owner username substring (case-insensitive). */
  owner?: string;
  cursor?: string;
  limit?: number;
}

export interface SetVisibilityPayload {
  visibility: 'private' | 'unlisted' | 'public';
}

export interface ImportDeckPayload {
  title: string;
  language: 'pl' | 'en';
  rawText: string;
  separator: 'tab' | 'comma' | 'semicolon';
  /** Default 'newline' (każda linia = karta). 'blank_line' gdy pytanie/odpowiedź zawiera
   *  embedded newlines (multi-line content). */
  cardSeparator?: 'newline' | 'blank_line';
  /** Gdy true: front'y z `...` konwertowane na cloze cards (1× → całe back, N× → split po `,`). */
  convertEllipsisToCloze?: boolean;
}

export const decksApi = {
  listMy: (params?: { folder?: number | null; q?: string }) => {
    const search = new URLSearchParams();
    if (params?.q) search.set('q', params.q);
    else if (params?.folder === null) search.set('folder', 'root');
    else if (typeof params?.folder === 'number') search.set('folder', String(params.folder));
    const qs = search.toString();
    return fetchJson<DeckListResponse>(qs ? `/decks?${qs}` : '/decks');
  },
  get: (id: number) => fetchJson<DeckResponse>(`/decks/${id}`),
  create: (payload: CreateDeckPayload) =>
    postJson<DeckResponse, CreateDeckPayload>('/decks', payload),
  patch: (id: number, payload: PatchDeckPayload) =>
    patchJson<DeckResponse, PatchDeckPayload>(`/decks/${id}`, payload),
  delete: (id: number) => deleteJson<void>(`/decks/${id}`),
  reset: (id: number) => postJson<void>(`/decks/${id}/reset`),
  addCollaborator: (deckId: number, body: AddCollaboratorPayload) =>
    postJson<DeckCollaboratorResponse, AddCollaboratorPayload>(
      `/decks/${deckId}/collaborators`,
      body,
      { suppressErrorToast: true },
    ),

  // === Public catalog + subscriptions ===
  listPublic: (query: PublicDecksQuery = {}) => {
    const search = new URLSearchParams();
    if (query.q) search.set('q', query.q);
    if (query.tags && query.tags.length > 0) search.set('tags', query.tags.join(','));
    if (query.owner) search.set('owner', query.owner);
    if (query.cursor) search.set('cursor', query.cursor);
    if (query.limit) search.set('limit', String(query.limit));
    const qs = search.toString();
    return fetchJson<PublicDeckListResponse>(qs ? `/decks/public?${qs}` : '/decks/public');
  },
  subscribe: (deckId: number) => postJson<void>(`/decks/${deckId}/subscribe`),
  unsubscribe: (deckId: number) => deleteJson<void>(`/decks/${deckId}/subscribe`),
  setVisibility: (deckId: number, payload: SetVisibilityPayload) =>
    patchJson<DeckResponse, SetVisibilityPayload>(`/decks/${deckId}/visibility`, payload),
  importDeck: (payload: ImportDeckPayload) =>
    postJson<DeckResponse, ImportDeckPayload>('/decks/import', payload),
};
